import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import PageHeader from '../components/PageHeader.jsx'
import Pagination from '../components/Pagination.jsx'
import ProjectPicker from '../components/ProjectPicker.jsx'
import StatusBadge from '../components/StatusBadge.jsx'
import { useAuth } from '../components/AuthProvider.jsx'
import { useInventory } from '../components/InventoryProvider.jsx'
import { CATEGORY_SUGGESTIONS, SEARCH_FIELDS, STOCK_FILTERS, VERIFICATION_FILTERS } from '../data/constants'
import { fetchPartsPage } from '../services/inventoryApi'
import { canManageParts } from '../utils/permissions'
import { uniqueSorted } from '../utils/search'
import { formatCurrentStock, needsReview, partTitle } from '../utils/stock'

const PAGE_SIZES = [20, 50, 100]

function parsePage(value) {
  const n = Number.parseInt(value, 10)
  return Number.isInteger(n) && n > 0 ? n : 1
}

function parseLimit(value) {
  const n = Number.parseInt(value, 10)
  return PAGE_SIZES.includes(n) ? n : 20
}

export default function InventoryPage() {
  const { user } = useAuth()
  const manageParts = canManageParts(user)
  const { parts, error: catalogError } = useInventory()
  const [params, setParams] = useSearchParams()
  const [query, setQuery] = useState(params.get('q') || '')
  const [field, setField] = useState(params.get('field') || 'all')
  const [category, setCategory] = useState(params.get('category') || 'all')
  const [manufacturer, setManufacturer] = useState(params.get('manufacturer') || 'all')
  const [projectId, setProjectId] = useState(params.get('projectId') || 'all')
  const [stock, setStock] = useState(params.get('stock') || 'all')
  const [verification, setVerification] = useState(params.get('verification') || 'all')
  const [page, setPage] = useState(parsePage(params.get('page')))
  const [limit, setLimit] = useState(parseLimit(params.get('limit')))
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [listLoading, setListLoading] = useState(true)
  const [listError, setListError] = useState('')

  const manufacturers = useMemo(
    () => uniqueSorted(parts.map((part) => part.manufacturer)),
    [parts],
  )

  const categories = useMemo(
    () => uniqueSorted([...CATEGORY_SUGGESTIONS, ...parts.map((part) => part.category)]),
    [parts],
  )

  function syncParams(next) {
    const merged = {
      q: query,
      field,
      category,
      manufacturer,
      projectId,
      stock,
      verification,
      page,
      limit,
      ...next,
    }
    const search = new URLSearchParams()
    Object.entries(merged).forEach(([key, value]) => {
      if (key === 'page' && Number(value) > 1) search.set('page', String(value))
      else if (key === 'limit' && Number(value) && Number(value) !== 20) search.set('limit', String(value))
      else if (value && value !== 'all' && key !== 'page' && key !== 'limit') search.set(key, value)
    })
    setParams(search, { replace: true })
  }

  function changeFilters(next) {
    if (next.q !== undefined) setQuery(next.q)
    if (next.field !== undefined) setField(next.field)
    if (next.category !== undefined) setCategory(next.category)
    if (next.manufacturer !== undefined) setManufacturer(next.manufacturer)
    if (next.projectId !== undefined) setProjectId(next.projectId || 'all')
    if (next.stock !== undefined) setStock(next.stock)
    if (next.verification !== undefined) setVerification(next.verification)
    setPage(1)
    syncParams({ ...next, page: 1 })
  }

  function changePage(nextPage) {
    setPage(nextPage)
    syncParams({ page: nextPage })
  }

  function changeLimit(nextLimit) {
    setLimit(nextLimit)
    setPage(1)
    syncParams({ limit: nextLimit, page: 1 })
  }

  useEffect(() => {
    let cancelled = false
    async function load() {
      setListLoading(true)
      setListError('')
      try {
        const result = await fetchPartsPage({
          q: query,
          field,
          category,
          manufacturer,
          projectId,
          stock,
          verification,
          page,
          limit,
        })
        if (cancelled) return
        setItems(result.items)
        setTotal(result.total)
        setTotalPages(result.totalPages)
        if (result.page !== page) {
          setPage(result.page)
          syncParams({ page: result.page })
        }
      } catch (err) {
        if (!cancelled) setListError(err.message || '재고 목록을 불러오지 못했습니다.')
      } finally {
        if (!cancelled) setListLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [query, field, category, manufacturer, projectId, stock, verification, page, limit])

  const error = listError || catalogError

  return (
    <div className="page">
      <PageHeader
        title="재고관리"
        description="모델명 검색을 우선으로 부품을 찾고 재고 상태를 확인합니다."
        actions={
          manageParts ? (
            <>
              <Link to="/inventory/review" className="btn btn--ghost btn--touch">
                재고 검수
              </Link>
              <Link to="/inventory/new" className="btn btn--primary btn--touch">
                신규부품 등록
              </Link>
            </>
          ) : null
        }
      />

      {error ? <p className="form-error">{error}</p> : null}

      <section className="filter-panel">
        <div className="search-row">
          <select
            className="input search-field"
            value={field}
            onChange={(e) => changeFilters({ field: e.target.value })}
            aria-label="검색 구분"
          >
            {SEARCH_FIELDS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
          <input
            className="input search-input"
            value={query}
            onChange={(e) => changeFilters({ q: e.target.value })}
            placeholder="모델명, 제조사, 품명, 관리번호 검색 (예: S803, Phoenix, X20)"
            aria-label="검색어"
          />
        </div>

        <div className="filter-row">
          <label className="field field--inline">
            <span>제품 분류</span>
            <select
              className="input"
              value={category}
              onChange={(e) => changeFilters({ category: e.target.value })}
            >
              <option value="all">전체</option>
              {categories.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>
          <label className="field field--inline">
            <span>제조사</span>
            <select
              className="input"
              value={manufacturer}
              onChange={(e) => changeFilters({ manufacturer: e.target.value })}
            >
              <option value="all">전체</option>
              {manufacturers.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>
          <label className="field field--inline field--project">
            <span>사용 프로젝트</span>
            <ProjectPicker
              multiple={false}
              activeOnly={false}
              value={projectId === 'all' ? '' : projectId}
              onChange={(id) => changeFilters({ projectId: id || 'all' })}
              placeholder="프로젝트 검색"
              emptyLabel="전체"
            />
          </label>
        </div>

        <div className="filter-toolbar">
          <div className="filter-group">
            <span className="filter-group__label">재고 상태</span>
            <div className="segmented" role="group" aria-label="재고 상태 필터">
              {STOCK_FILTERS.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  className={`segmented__btn ${stock === item.value ? 'is-active' : ''}`}
                  onClick={() => changeFilters({ stock: item.value })}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
          <label className="filter-group filter-group--select">
            <span className="filter-group__label">검수 상태</span>
            <select
              className="input filter-select"
              value={verification}
              onChange={(e) => changeFilters({ verification: e.target.value })}
              aria-label="검수 상태 필터"
            >
              {VERIFICATION_FILTERS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      <p className="result-count">
        {listLoading ? '불러오는 중...' : `총 ${total.toLocaleString('ko-KR')}개 품목`}
      </p>

      {items.length === 0 ? (
        <p className="empty">{listLoading ? '재고 목록을 불러오는 중입니다.' : '검색 조건에 맞는 부품이 없습니다.'}</p>
      ) : (
        <>
          <div className="card-list">
            {items.map((part) => (
              <article key={part.id} className="part-card">
                <Link to={`/inventory/${part.id}`} className="part-card__main">
                  <div className="part-card__top">
                    <span className="muted">
                      {part.partCode} · {part.category}
                    </span>
                    <span className="badge-row">
                      <StatusBadge kind="verification" part={part} />
                      <StatusBadge part={part} />
                    </span>
                  </div>
                  <strong className="model-name">{partTitle(part)}</strong>
                  <span>
                    {part.manufacturer} · {part.name}
                  </span>
                  <div className="part-card__qty">
                    <b>{formatCurrentStock(part)}</b>
                  </div>
                </Link>
                {needsReview(part) && manageParts ? (
                  <div className="part-card__actions">
                    <Link to={`/inventory/review?part=${part.id}`} className="btn btn--primary btn--touch">
                      검수
                    </Link>
                  </div>
                ) : null}
              </article>
            ))}
          </div>

          <div className="table-wrap">
            <table className="data-table inventory-table">
              <thead>
                <tr>
                  <th>관리번호</th>
                  <th>분류</th>
                  <th>제조사</th>
                  <th>모델</th>
                  <th>품명</th>
                  <th>현재재고</th>
                  <th>단위</th>
                  <th>재고상태</th>
                  <th>검수상태</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((part) => (
                  <tr key={part.id}>
                    <td>{part.partCode}</td>
                    <td>{part.category}</td>
                    <td>{part.manufacturer}</td>
                    <td>
                      <Link to={`/inventory/${part.id}`} className="model-link model-name">
                        {partTitle(part)}
                      </Link>
                    </td>
                    <td>{part.name}</td>
                    <td className="td-qty">{formatCurrentStock(part)}</td>
                    <td>{part.unit}</td>
                    <td>
                      <StatusBadge part={part} />
                    </td>
                    <td>
                      {manageParts ? (
                        <Link
                          to={needsReview(part) ? `/inventory/review?part=${part.id}` : '/inventory/review?filter=done'}
                        >
                          <StatusBadge kind="verification" part={part} />
                        </Link>
                      ) : (
                        <StatusBadge kind="verification" part={part} />
                      )}
                    </td>
                    <td>
                      {needsReview(part) && manageParts ? (
                        <Link to={`/inventory/review?part=${part.id}`} className="btn btn--primary inventory-review-btn">
                          검수
                        </Link>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Pagination
        page={page}
        totalPages={totalPages}
        total={total}
        limit={limit}
        disabled={listLoading}
        onPageChange={changePage}
        onLimitChange={changeLimit}
      />
    </div>
  )
}
